import { useEffect, useRef } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      heroRef.current.style.setProperty('--mouse-x', `${x}%`);
      heroRef.current.style.setProperty('--mouse-y', `${y}%`);
    };
    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    return () => el?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
    >
      {/* Deep background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-[#1a1520] to-gray-950"></div>

      {/* Neural network canvas — primary layer */}
      <NeuralNetworkBg
        color="212,120,158"
        nodeCount={55}
        opacity={0.5}
        speed={0.8}
        variant="dark"
      />

      {/* Second neural layer — different color for depth */}
      <NeuralNetworkBg
        color="154,123,212"
        nodeCount={30}
        opacity={0.25}
        speed={0.5}
        variant="dark"
      />

      {/* Third neural layer — teal accents */}
      <NeuralNetworkBg
        color="77,184,199"
        nodeCount={20}
        opacity={0.2}
        speed={0.6}
        variant="dark"
      />

      {/* Soft animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute w-[800px] h-[800px] rounded-full animate-float opacity-15"
          style={{
            background:
              'radial-gradient(circle, rgba(212,120,158,0.35) 0%, transparent 70%)',
            top: '-20%',
            right: '-10%',
          }}
        ></div>
        <div
          className="absolute w-[600px] h-[600px] rounded-full animate-float-slow opacity-12"
          style={{
            background:
              'radial-gradient(circle, rgba(154,123,212,0.3) 0%, transparent 70%)',
            bottom: '-15%',
            left: '-5%',
            animationDelay: '2s',
          }}
        ></div>
        <div
          className="absolute w-[500px] h-[500px] rounded-full animate-float opacity-10"
          style={{
            background:
              'radial-gradient(circle, rgba(77,184,199,0.3) 0%, transparent 70%)',
            top: '30%',
            left: '50%',
            animationDelay: '4s',
          }}
        ></div>
        <div
          className="absolute w-[400px] h-[400px] rounded-full animate-pulse-glow opacity-10"
          style={{
            background:
              'radial-gradient(circle, rgba(232,149,106,0.35) 0%, transparent 70%)',
            top: '10%',
            left: '20%',
          }}
        ></div>
      </div>

      {/* Mouse-following radial glow */}
      <div
        className="absolute inset-0 opacity-15 transition-all duration-1000 pointer-events-none"
        style={{
          background:
            'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(212,120,158,0.12), transparent 60%)',
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 text-center pt-20">
        {/* Logo */}
        <div className="mb-10 animate-fade-in-up">
          <img
            src="https://public.readdy.ai/ai/img_res/070ad906-f77e-457c-bac3-5ff241a90757.png"
            alt="Brain Wash"
            className="h-28 lg:h-36 w-auto mx-auto drop-shadow-2xl animate-float-slow"
          />
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight text-white mb-8 leading-[0.95] animate-fade-in-up stagger-1">
          Your brain was<br />
          <span className="gradient-text-animated">never meant</span><br />
          for this modern world of ovewhelm.
        </h1>

        <p className="text-xl lg:text-2xl font-medium text-white/60 mb-4 tracking-tight animate-fade-in-up stagger-2">
          It&apos;s time for a positive <span className="gradient-text font-bold">brain wash</span>.
        </p>

        <p className="text-base lg:text-lg text-white/40 max-w-2xl mx-auto mb-14 leading-relaxed animate-fade-in-up stagger-3">
          &nbsp;Rebuild your reward system. Reclaim your focus. Regulate your nervous system. Rewire your mind &amp; body. Release emotions &amp; stress.
        </p>

        <div className="animate-fade-in-up stagger-4">
          <a
            href="#cta"
            className="inline-block px-14 py-5 bg-gradient-to-r from-brand-orange via-brand-pink to-brand-violet text-white text-base font-semibold rounded-full hover:shadow-2xl hover:shadow-brand-pink/30 hover:scale-105 transition-all duration-500 whitespace-nowrap cursor-pointer animate-gradient-shift"
          >
            Begin Reset
          </a>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white to-transparent"></div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white/40 rounded-full"></div>
        </div>
      </div>
    </section>
  );
}
